import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, X, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  fetchBrandAffinity,
  deleteBrandAffinity,
  clearBrandAffinity,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * BrandAffinityCard — algorithm override for the user.
 *
 * Surfaces every brand Trouvé has learned the user reaches for, with
 * per-brand "Remove" and a bulk "Reset learning" CTA. Lets the user
 * course-correct if a curiosity click planted the wrong signal, or
 * start fresh if their taste has changed.
 *
 * Empty state explicitly explains HOW the engine learns ("clicks on
 * Shop, saves to Arsenal") so users understand what they're seeing.
 * Honest transparency beats clever wording every time.
 */
const BrandAffinityCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(() => new Set());
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchBrandAffinity();
        if (alive) {
          setData(res);
          track("brand_affinity_viewed", {
            item_count: (res?.items || []).length,
            total_signals: res?.total_signals || 0,
          });
        }
      } catch {
        if (alive) setData({ items: [], total_signals: 0, min_signals_for_badge: 3 });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleRemove = async (brand) => {
    setRemoving((prev) => new Set(prev).add(brand));
    try {
      const res = await deleteBrandAffinity(brand);
      setData(res);
      track("brand_affinity_brand_removed", { brand });
      toast.success(t("brandAffinity.forgetSuccess", { brand }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("brandAffinity.forgetFail"));
    } finally {
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(brand);
        return next;
      });
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t("brandAffinity.confirmReset"))) {
      return;
    }
    setClearing(true);
    try {
      const res = await clearBrandAffinity();
      setData(res);
      track("brand_affinity_cleared");
      toast.success(t("brandAffinity.resetDone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("brandAffinity.resetFail"));
    } finally {
      setClearing(false);
    }
  };

  const items = data?.items || [];
  const totalSignals = data?.total_signals || 0;
  const threshold = data?.min_signals_for_badge || 3;
  const learningActive = totalSignals >= threshold && items.length > 0;

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 sm:p-7 soft-shadow mb-5"
      data-testid="brand-affinity-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-xl font-medium text-[#2C2123]"
            data-testid="brand-affinity-heading"
          >
            {t("brandAffinity.heading")}
          </h3>
          <p className="text-sm text-[#5C4B51] leading-relaxed mt-1">
            <Trans
              i18nKey="brandAffinity.intro"
              components={{ em: <em /> }}
            />
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A7D82] py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("brandAffinity.loading")}
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-2xl bg-[#FAF5F1] border border-dashed border-[#EADDD7] p-6 text-center"
          data-testid="brand-affinity-empty"
        >
          <p className="text-sm text-[#5C4B51]">
            <Trans
              i18nKey="brandAffinity.emptyBody"
              values={{ threshold }}
              components={{ em: <em /> }}
            />
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[#9B6A6C] font-medium">
              {learningActive ? (
                totalSignals === 1
                  ? t("brandAffinity.active", { count: totalSignals })
                  : t("brandAffinity.activeMany", { count: totalSignals })
              ) : (
                t("brandAffinity.building", { count: totalSignals, threshold })
              )}
            </div>
            <button
              type="button"
              onClick={handleReset}
              disabled={clearing}
              className="inline-flex items-center gap-1 text-xs text-[#9B6A6C] hover:text-[#7d5455] underline underline-offset-2 disabled:opacity-50"
              data-testid="brand-affinity-reset"
            >
              {clearing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCcw className="h-3 w-3" />
              )}
              {t("brandAffinity.resetBtn")}
            </button>
          </div>
          <ul
            className="space-y-2"
            data-testid="brand-affinity-items"
          >
            {items.map((it) => (
              <motion.li
                key={it.brand}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#FAF5F1] border border-[#EADDD7]"
                data-testid={`brand-affinity-item-${it.brand}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#2C2123] truncate">
                    {it.brand}
                  </div>
                  <div className="text-xs text-[#8A7D82] mt-0.5">
                    {it.signals === 1
                      ? t("brandAffinity.signalsOne", { count: it.signals })
                      : t("brandAffinity.signalsMany", { count: it.signals })}
                    {it.last_source === "arsenal_add" && (
                      <span> · {t("brandAffinity.fromArsenal")}</span>
                    )}
                    {it.last_source === "shop_click" && (
                      <span> · {t("brandAffinity.fromShop")}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(it.brand)}
                  disabled={removing.has(it.brand)}
                  className="p-1.5 rounded-full hover:bg-white text-[#8A7D82] hover:text-[#9B6A6C] transition-colors disabled:opacity-50"
                  data-testid={`brand-affinity-remove-${it.brand}`}
                  aria-label={t("brandAffinity.forgetAria", { brand: it.brand })}
                >
                  {removing.has(it.brand) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </button>
              </motion.li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default BrandAffinityCard;
